import React, {useContext} from 'react'
import {Typography} from '@material-ui/core';
import PropTypes from 'prop-types';
import GridBlock from 'components/GridBlock';
import ExpansionPanelTitleAvatar from "components/ExpansionPanelTitleAvatar";
import SignatureIcon from "components/icon/SignatureIcon";
import Button from "components/Button";
import DemandOffersOfferService from "services/DemandOffersOfferService";
import {useParams} from "react-router";
import useSignature from "hooks/useSignature";
import {getSectionVariant} from "../../../../constants/StageSections";
import {OfferStatusType} from "../../../common/constants/OfferStatusType";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CancelIcon from "@material-ui/icons/Cancel";
import {makeStyles} from "@material-ui/styles";
import useLoadingScreen from "hooks/useLoadingScreen";
import DemandOffersService from "services/DemandOffersService";
import isResponseValid from "libs/isResponseValid";
import {ContractorPasswordState} from "constants/ContractorPasswordState";
import {simpleAlert} from "libs/AlertNotification";
import {simpleConfirmNoTranslate} from "libs/ConfirmNotification";
import {DemandValidationSectionStatus} from "constants/DemandValidationSectionStatus";
import moment from "libs/moment";
import DateFormats from "constants/DateFormats";
import OfferStageTranslationsContext from "context/OfferStageTranslationsContext";
import SupportModeContext from "context/SupportModeContext";
import PurchaserOfferContext from "context/PurchaserOfferContext";

const useStyle = makeStyles(theme => ({
    marginRight: {
        marginRight: theme.spacing(2)
    },
    offerDate: {
        marginLeft: theme.spacing(4),
        fontStyle: "italic"
    }
}))

function OfferPlacedSectionTitle(
    {
        section,
        expanded,
    }) {
    const classes = useStyle();
    const params = useParams();
    const demandId = params.id;
    const stageId = params.stageId;
    const stageTr = useContext(OfferStageTranslationsContext);
    const supportMode = useContext(SupportModeContext);
    const {doSignDocContent, breakSigning} = useSignature();
    const {hideLoadingScreen, showLoadingScreen} = useLoadingScreen();
    const purchaserOfferContext = useContext(PurchaserOfferContext);
    const isPurchaser = purchaserOfferContext?.contractorDepartmentId > 0;

    const withdrawWholeOffer = (e) => {
        e.stopPropagation();
        if (section.digitalSign) {
            doSignDocContent(
                section.signatureTypes,
                async (sigType) => {
                    const result = await DemandOffersOfferService.getDocumentToWithdraw(demandId, stageId, {positionIds: null, signatureType: sigType});
                    if (!result)
                        breakSigning();
                    return result;

                },
                (signatureType, signature, signatureFileId) => {
                    breakSigning();
                    submitWithdrawOffer(null, signatureType, signature, signatureFileId)
                },
                (error) => {
                    console.error(error)
                    simpleAlert("signature_error");
                    breakSigning();
                },
                () => {
                    breakSigning();
                },
                async () => {
                    const result = await DemandOffersOfferService.requireWithdrawEncryption(demandId, stageId, null);
                    if (!result) {
                        breakSigning();
                    } else if (result.requireEncryption) {
                        return result.roundId;
                    } else {
                        return null;
                    }
                }
            )
        } else {
            simpleConfirmNoTranslate(stageTr("withdraw_offer_confirm"), submitWithdrawOffer)

        }
    }

    const submitWithdrawOffer = (positionIds, signatureType, signature, signatureFileId) => {
        showLoadingScreen();
        DemandOffersOfferService.withdrawOffer(demandId, stageId, {positionIds, signatureType, signature, signatureFileId}, section.onePositionMode, stageTr)
            .finally(hideLoadingScreen)
    }

    const stopSigningOperation = (e) => {
        e.stopPropagation();
        simpleConfirmNoTranslate(stageTr("sign_withdraw_form_stop_confirm"), () => {
                showLoadingScreen();
                DemandOffersOfferService.stopSigningOperation(demandId, stageId).finally(hideLoadingScreen)
            }
        )
    }

    const signWithdrawOffer = (e) => {
        e.stopPropagation();

        (async () => {
            showLoadingScreen();
            const result = await DemandOffersOfferService.requireWithdrawEncryption(demandId, stageId, null);
            if (!result) {
                hideLoadingScreen();
            } else if (result.requireEncryption) {
                try {
                    const passResp = await DemandOffersService.getPasswordStatus(demandId, stageId);
                    if (isResponseValid((passResp))) {
                        if (passResp.result.state === ContractorPasswordState.HAS_PASSWORD_STORED) {
                            submitSignWithdrawOffer(result.roundId);
                        } else {
                            DemandOffersService.openContractorPasswordPopup(() => submitSignWithdrawOffer(result.roundId), () => null)
                        }
                    }
                } catch (e) {
                    console.error(e);
                }
                hideLoadingScreen();
            } else {
                submitSignWithdrawOffer(null);
                hideLoadingScreen();
            }


        })()
    }

    const submitSignWithdrawOffer = (encryptionRoundId) => {

        doSignDocContent(
            section.signatureTypes,
            async (sigType) => {
                const result = await DemandOffersOfferService.getDocumentToWithdraw(demandId, stageId, {positionIds: null, signatureType: sigType});
                if (!result)
                    breakSigning();
                return result;

            },
            (signatureType, signature, signatureFileId) => {
                breakSigning();
                showLoadingScreen()
                DemandOffersOfferService.signWithdraw(demandId, stageId, {
                    positionIds: null,
                    signatureType,
                    signature,
                    signatureFileId
                }, section.onePositionMode, stageTr)
                    .finally(hideLoadingScreen)
            },
            (error) => {
                console.error(error)
                simpleAlert("signature_error");
                breakSigning();
            },
            () => {
                breakSigning();
            },
            async () => encryptionRoundId
        )

    }

    return (
        <GridBlock alignItems="center" justify="space-between">
            <GridBlock alignItems="center">
                <ExpansionPanelTitleAvatar variant={getSectionVariant(section)}/>
                <Typography variant='h6'>
                    {section.name}
                </Typography>
                {!!section.offerDate &&
                    <Typography className={classes.offerDate}>
                        {stageTr("offer_date")}: {moment(section.offerDate).format(DateFormats.DATETIME_WITH_SECONDS_FORMAT)}
                    </Typography>
                }
            </GridBlock>
            {(section.status !== DemandValidationSectionStatus.DISABLED && !section.offerCollectionEnded && section.contractorOfferStatus === OfferStatusType.WAITING_WITHDRAW_SIGNATURE && !isPurchaser) && <>
                <Button Icon={CancelIcon} onClick={stopSigningOperation} variant='outlined' disabled={supportMode}
                        className={classes.marginRight}>{stageTr("sign_withdraw_form_stop")}</Button>
                <Button Icon={SignatureIcon} onClick={signWithdrawOffer} disabled={supportMode}>{stageTr("sign_withdraw_form")}</Button>
            </>
            }
            {(section.status !== DemandValidationSectionStatus.DISABLED && !section.offerCollectionEnded && section.contractorOfferStatus === OfferStatusType.NORMAL && !isPurchaser) &&
                <Button onClick={withdrawWholeOffer} variant="outlined" Icon={HighlightOffIcon} disabled={supportMode}>{stageTr("withdraw_offer_full")}</Button>
            }
        </GridBlock>
    )

}

OfferPlacedSectionTitle.propTypes = {
    expanded: PropTypes.bool.isRequired,
    section: PropTypes.shape({
        section: PropTypes.string.isRequired
    }).isRequired
}

export default OfferPlacedSectionTitle;